<script setup>
import { ref, onMounted, onUnmounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { depositConfirmation } from '@/services/api_services';

const route = useRoute();
const router = useRouter();
const paymentStatus = ref('pending');
const loading = ref(true);
const error = ref(null);
const message = ref(route.query.initialMessage || 'Checking payment status...');
const retryCount = ref(0);
const maxRetries = 12;
const retryInterval = 3000;
let intervalId = null;

const checkPaymentStatus = async () => {
  try {
    const response = await depositConfirmation(route.params.paymentId);

    if (response.confirmedAt !== null) {
      paymentStatus.value = 'success';
      message.value = 'Payment confirmed! Thank you for your contribution.';
      clearInterval(intervalId);
    } else if (response.failureReason !== null) {
      paymentStatus.value = 'failed';
      message.value = response.failureReason || 'Payment failed. Please try again.';
      clearInterval(intervalId);
    } else {
      retryCount.value += 1;
      if (retryCount.value >= maxRetries) {
        paymentStatus.value = 'timeout';
        message.value = 'Payment confirmation timed out. Please check your M-PESA messages.';
        clearInterval(intervalId);
      } else {
        message.value = response.message || 'Still waiting for payment confirmation...';
      }
    }
  } catch (e) {
    console.error('Error checking payment:', e);
    error.value = 'Error checking payment status.';
    if (retryCount.value >= maxRetries) {
      clearInterval(intervalId);
    }
  } finally {
    loading.value = false;
  }
};

const retryConfirmation = () => {
  loading.value = true;
  error.value = null;
  message.value = 'Retrying confirmation...';
  checkPaymentStatus();
};

onMounted(() => {
  checkPaymentStatus();
  intervalId = setInterval(checkPaymentStatus, retryInterval);
});

onUnmounted(() => {
  clearInterval(intervalId);
});
</script>

<template>
  <div class="page-container">
    <div class="confirmation-container">
      <h1 class="page-title">Payment Confirmation</h1>

      <div class="status-card" :class="paymentStatus">
        <div v-if="paymentStatus === 'pending'" class="pending-status">
          <svg class="spinner" viewBox="0 0 50 50">
            <circle class="path" cx="25" cy="25" r="20" fill="none" stroke-width="5"></circle>
          </svg>
          <h2>Processing Payment</h2>
          <p>{{ message }}</p>
          <p class="retry-count">Checking... ({{ retryCount + 1 }}/{{ maxRetries }})</p>
        </div>

        <div v-else-if="paymentStatus === 'success'" class="success-status">
          <svg xmlns="http://www.w3.org/2000/svg" class="icon" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clip-rule="evenodd" />
          </svg>
          <h2>Payment Successful!</h2>
          <p>{{ message }}</p>
          <p class="fundraiser-info">Thank you for supporting {{ route.params.fundraiserName }}</p>
        </div>

        <div v-else class="failed-status">
          <svg xmlns="http://www.w3.org/2000/svg" class="icon" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                  clip-rule="evenodd" />
          </svg>
          <h2>Payment Not Confirmed</h2>
          <p>{{ message }}</p>

          <p class="timeout-message">
            If you’re sure you’ve completed the payment, you can retry the confirmation.
          </p>

          <button class="retry-button" @click="retryConfirmation" :disabled="loading">
            Retry Confirmation
          </button>

          <p v-if="loading" style="margin-top: 1rem;">Retrying confirmation...</p>

          <p v-if="paymentStatus === 'timeout'" class="timeout-message">
            The payment confirmation process has timed out. Please check your M-PESA messages.
          </p>
        </div>

        <button
            class="action-button"
            @click="router.replace({
      name: 'FundraiserDetails',
      params: { id: route.params.fundraiserId }
    })">
          Back to Fundraiser
        </button>
      </div>

      <div v-if="error" class="error-message">
        {{ error }}
      </div>
    </div>
  </div>
</template>

<style scoped>
.page-container {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  background: linear-gradient(135deg, rgba(203, 217, 196, 1) 0%, rgba(249, 247, 194, 1) 50%, rgba(249, 213, 206, 1) 100%);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
}

.confirmation-container {
  width: 100%;
  max-width: 480px;
  padding: 2.5rem;
  background: rgba(255, 255, 255, 0.96);
  border-radius: 16px;
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.08), 0 4px 12px rgba(0, 0, 0, 0.05);
  backdrop-filter: blur(8px);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.status-card {
  text-align: center;
  padding: 2rem;
  border-radius: 12px;
  margin-bottom: 1.5rem;
}

.pending-status {
  color: #d97706;
}

.success-status {
  color: #065f46;
  background-color: #ecfdf5;
}

.failed-status {
  color: #b91c1c;
  background-color: #fef2f2;
}

.icon {
  width: 3rem;
  height: 3rem;
  margin-bottom: 1rem;
}

.spinner {
  width: 3rem;
  height: 3rem;
  margin-bottom: 1rem;
  animation: rotate 2s linear infinite;
}

.path {
  stroke: #d97706;
  stroke-linecap: round;
  animation: dash 1.5s ease-in-out infinite;
}

@keyframes rotate {
  100% {
    transform: rotate(360deg);
  }
}

@keyframes dash {
  0% {
    stroke-dasharray: 1, 150;
    stroke-dashoffset: 0;
  }

  50% {
    stroke-dasharray: 90, 150;
    stroke-dashoffset: -35;
  }

  100% {
    stroke-dasharray: 90, 150;
    stroke-dashoffset: -124;
  }
}

.retry-count,
.fundraiser-info,
.timeout-message {
  font-size: 0.9rem;
  margin-top: 1rem;
  color: #4b5563;
}

.action-button {
  margin-top: 2rem;
  padding: 0.75rem 1.5rem;
  background-color: #10b981;
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s;
}

.action-button:hover {
  background-color: #059669;
}

.retry-button {
  margin-top: 1rem;
  padding: 0.5rem 1rem;
  background-color: #2563eb;
  color: white;
  border: none;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s;
}

.retry-button:hover {
  background-color: #1d4ed8;
}

.error-message {
  color: #b91c1c;
  text-align: center;
  margin-top: 1rem;
}
</style>
